import dayjs from 'dayjs';

import type { CastAiWebCookie } from 'core/cookies';

export type ReportingCookieEligibilityAction =
  | 'noop'
  | 'clear-only'
  | 'mark-and-clear';

/** Only mark orgs created within this window (hours, not long-lived orgs). */
export const REPORTING_ELIGIBILITY_MAX_ORG_AGE_HOURS = 24;

const isOrgRecentEnoughForReportingEligibility = (
  organizationCreatedAt: string | undefined,
  nowMs: number
): boolean => {
  // dayjs(undefined) is "now" and valid — require an explicit timestamp.
  if (!organizationCreatedAt) {
    return false;
  }
  const createdAt = dayjs(organizationCreatedAt);
  if (!createdAt.isValid()) {
    return false;
  }
  const oldestAllowed = dayjs(nowMs).subtract(
    REPORTING_ELIGIBILITY_MAX_ORG_AGE_HOURS,
    'hour'
  );
  return !createdAt.isBefore(oldestAllowed);
};

/** Pure helper for `_cast_redirect_context` when product is reporting. */
export const resolveReportingCookieEligibilityAction = ({
  cookie,
  isEligible,
  reportingTrialEnabled,
  canFetchReportingMonetization,
  firstLogin,
  organizationCreatedAt,
  nowMs = Date.now(),
}: {
  cookie: CastAiWebCookie | undefined;
  isEligible: boolean;
  reportingTrialEnabled: boolean;
  /** False for cluster-only users — mark-eligible would 403. */
  canFetchReportingMonetization: boolean;
  /** Existing accounts must never be auto-marked via marketing cookie. */
  firstLogin: boolean;
  /** Older orgs must never be flipped to Cast Radar via sticky cookie. */
  organizationCreatedAt: string | undefined;
  nowMs?: number;
}): ReportingCookieEligibilityAction => {
  if (cookie?.product !== 'reporting') {
    return 'noop';
  }
  if (!reportingTrialEnabled || isEligible) {
    return 'clear-only';
  }
  if (!canFetchReportingMonetization) {
    return 'noop';
  }
  if (
    !firstLogin ||
    !isOrgRecentEnoughForReportingEligibility(organizationCreatedAt, nowMs)
  ) {
    return 'clear-only';
  }
  return 'mark-and-clear';
};
